import axios from "axios";
import Cookies from "js-cookie";
import {
  assertValidSessionOrRedirect,
  getStoredAccessToken,
  isAuthFailureError,
  redirectToLogin,
} from "../utils/authSession";
import { buildOrganizationContextHeaders, ensureSuperAdminOrganizationContext, isAdminOrganizationListRequest, isPlatformSuperAdmin, getSelectedOrganizationId } from "../utils/organizationContext";
import {
  invalidateCachedRequestPrefix,
} from "./callCenterRequestCache";
import { getAppTypeHeader, isDesktopApp } from "../utils/isDesktop";
import { getDesktopConfig } from "../utils/desktopBridge";
import { isCallingRelatedApiUrl } from "../utils/callingApiGuard";
import { isCallingEnabled } from "../utils/orgFeatures";

export const CALL_CENTER_SUCCESS_CODE = 600;
export const CALL_CENTER_ACCESS_DENIED_CODE = 608;

/**
 * @readonly
 * @enum {string}
 */
export const CallType = {
  MANUAL: "MANUAL",
  AI_AUTOMATED: "AI_AUTOMATED",
};

/**
 * @readonly
 * @enum {string}
 */
export const CallStatus = {
  INITIATED: "INITIATED",
  RINGING: "RINGING",
  ANSWERED: "ANSWERED",
  FAILED: "FAILED",
  COMPLETED: "COMPLETED",
};

/**
 * @typedef {Object} ManualCallStatusUpdateRequest
 * @property {'INITIATED'|'RINGING'|'ANSWERED'|'FAILED'|'COMPLETED'} status
 * @property {string} [eventTime] ISO-8601
 * @property {string} [recordingUrl]
 * @property {string} [summary]
 */

/**
 * @typedef {Object} ManualCallStatusByAccountRequest
 * @property {string} accountNo
 * @property {'INITIATED'|'RINGING'|'ANSWERED'|'FAILED'|'COMPLETED'} status
 * @property {string} [eventTime] ISO-8601
 * @property {string} [recordingUrl]
 * @property {string} [summary]
 */

/**
 * @readonly
 * @enum {string}
 */
export const TicketPriority = {
  LOW: "LOW",
  MEDIUM: "MEDIUM",
  HIGH: "HIGH",
  CRITICAL: "CRITICAL",
};

/**
 * @readonly
 * @enum {string}
 */
export const DefaultTicketStatusKey = {
  OPEN: "OPEN",
  FOLLOW_UP: "FOLLOW_UP",
  PAID: "PAID",
  FAILED: "FAILED",
  CALLBACK_REQUIRED: "CALLBACK_REQUIRED",
};

/**
 * @typedef {Object} ApiEnvelopeHeader
 * @property {number} code
 */

/**
 * @typedef {Object} ApiEnvelopeBody
 * @property {*} [value]
 * @property {string} [error]
 */

/**
 * @typedef {Object} ApiEnvelope
 * @property {ApiEnvelopeHeader} [header]
 * @property {ApiEnvelopeBody} [body]
 */

export const getCallCenterBaseUrl = () => {
  if (isDesktopApp()) {
    // Desktop always calls the Electron API proxy (5199), which forwards to the real backend.
    // Do NOT use REACT_APP_BASE_BACKEND_API_URL here — that is for browser-only builds.
    return cachedDesktopProxyUrl || "http://127.0.0.1:5199/";
  }
  const base = process.env.REACT_APP_BASE_BACKEND_API_URL || "";
  return base.endsWith("/") ? base : `${base}/`;
};

let cachedDesktopProxyUrl = null;

export const initDesktopApiBase = async () => {
  if (!isDesktopApp()) {
    return getCallCenterBaseUrl();
  }
  const config = await getDesktopConfig();
  if (config?.apiProxyUrl) {
    cachedDesktopProxyUrl = config.apiProxyUrl.endsWith("/")
      ? config.apiProxyUrl
      : `${config.apiProxyUrl}/`;
    callCenterClient.defaults.baseURL = `${cachedDesktopProxyUrl}api/`;
  }
  return getCallCenterBaseUrl();
};

/**
 * Same pattern as other service modules (e.g. Task.js, Configuration.js):
 * `Authorization: Bearer ${Cookies.get("accessToken")}`.
 * @param {Record<string, string>} [extraHeaders]
 * @returns {Record<string, string>}
 */
export const buildAuthHeaders = (extraHeaders = {}) => {
  const token = getStoredAccessToken();
  const headers = { ...buildOrganizationContextHeaders(), ...extraHeaders };
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  return headers;
};

export const isSuperAdminUser = () => {
  try {
    return Cookies.get("userType") === "super_admin";
  } catch {
    return false;
  }
};

export const getCallCenterApiErrorMessage = (envelope, fallbackMessage = "Request failed") => {
  if (!envelope || typeof envelope !== "object") {
    return fallbackMessage;
  }
  const bodyError = envelope?.body?.error;
  const bodyValue = envelope?.body?.value;
  if (typeof bodyError === "string" && bodyError.trim()) return bodyError;
  if (typeof bodyValue === "string" && bodyValue.trim()) return bodyValue;
  return fallbackMessage;
};

/**
 * @template T
 * @param {ApiEnvelope} envelope
 * @param {string} [fallbackMessage]
 * @returns {T}
 */
export const unwrapEnvelopeValue = (envelope, fallbackMessage = "Request failed") => {
  const code = envelope?.header?.code;
  if (code === CALL_CENTER_SUCCESS_CODE) {
    return envelope?.body?.value;
  }

  const error = new Error(getCallCenterApiErrorMessage(envelope, fallbackMessage));
  error.responseCode = code;
  error.envelope = envelope;
  throw error;
};

const callCenterClient = axios.create({
  baseURL: `${getCallCenterBaseUrl()}api/`,
});

callCenterClient.interceptors.request.use(async (config) => {
  if (!assertValidSessionOrRedirect()) {
    return Promise.reject(Object.assign(new Error("Session expired"), { isSessionExpired: true }));
  }

  // Re-check after any async work in this interceptor (e.g. super-admin org bootstrap).
  if (!getStoredAccessToken()) {
    return Promise.reject(Object.assign(new Error("Session expired"), { isSessionExpired: true }));
  }

  const token = getStoredAccessToken();
  if (!token) {
    return Promise.reject(new Error("Session expired"));
  }

  if (
    isPlatformSuperAdmin()
    && !getSelectedOrganizationId()
    && !isAdminOrganizationListRequest(config)
  ) {
    await ensureSuperAdminOrganizationContext();
  }

  const bearer = `Bearer ${token}`;
  const orgHeaders = buildOrganizationContextHeaders();
  const appTypeHeader = { "X-App-Type": getAppTypeHeader() };
  if (config.headers && typeof config.headers.set === "function") {
    config.headers.set("Authorization", bearer);
    Object.entries({ ...orgHeaders, ...appTypeHeader }).forEach(([key, value]) => {
      if (!config.headers.get(key)) {
        config.headers.set(key, value);
      }
    });
  } else {
    const next = { ...(config.headers || {}) };
    next.Authorization = bearer;
    Object.entries({ ...orgHeaders, ...appTypeHeader }).forEach(([key, value]) => {
      if (!next[key]) {
        next[key] = value;
      }
    });
    config.headers = next;
  }
  return config;
});

callCenterClient.interceptors.response.use(
  (response) => response,
  (error) => {
    const envelope = error?.response?.data;
    if (envelope?.header?.code) {
      const wrapped = new Error(getCallCenterApiErrorMessage(envelope, error.message));
      wrapped.responseCode = envelope.header.code;
      wrapped.httpStatus = error?.response?.status;
      wrapped.envelope = envelope;
      if (isAuthFailureError(wrapped)) {
        invalidateCachedRequestPrefix("tickets:");
        invalidateCachedRequestPrefix("calls:");
        redirectToLogin();
      }
      return Promise.reject(wrapped);
    }

    if (error?.isSessionExpired || isAuthFailureError(error)) {
      invalidateCachedRequestPrefix("tickets:");
      invalidateCachedRequestPrefix("calls:");
      redirectToLogin();
    }

    return Promise.reject(error);
  }
);

export const requestCallCenter = async (config) => {
  const url = config?.url || "";
  if (isCallingRelatedApiUrl(url) && !isCallingEnabled()) {
    const error = new Error("Calling is disabled for this organization.");
    error.code = "CALLING_DISABLED";
    error.responseCode = 608;
    error.httpStatus = 403;
    return Promise.reject(error);
  }

  const response = await callCenterClient.request(config);
  return response.data;
};

const parseBlobErrorMessage = async (blob, fallbackMessage) => {
  try {
    const text = await blob.text();
    const parsed = JSON.parse(text);
    return getCallCenterApiErrorMessage(parsed, fallbackMessage);
  } catch {
    return fallbackMessage;
  }
};

export const downloadBlobAsFile = async ({ url, fileName, fallbackMessage = "Download failed" }) => {
  const response = await axios.get(url, {
    responseType: "blob",
    headers: buildAuthHeaders(),
  });
  const blob = response.data;
  const contentType = response.headers?.["content-type"] || "";
  if (contentType.includes("application/json") || blob.type.includes("application/json")) {
    throw new Error(await parseBlobErrorMessage(blob, fallbackMessage));
  }
  const objectUrl = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = objectUrl;
  link.download = fileName || "download";
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(objectUrl);
};
